import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild, DestroyRef, inject, signal, input, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EditSelectable, EditSelectInputComponent } from '../edit-select-form-control/edit-select-form-control.component';
import { FormControl, FormGroup } from '@angular/forms';
import { LinkableService } from '../../services/entity-link/linkable/linkable.service';
import { LinkableFilter } from '../../entities/entityLink/linkableFilter';
import { Linkable } from '../../entities/entityLink/linkable';
import { ApolloService } from '../../services/apollo.service';
import { GET_LINKED_ENTITIES_QUERY, getLinkedEntitiesResponse } from './graphql';
import { catchError, map, take, tap } from 'rxjs/operators';
import { EntityLink } from '../../entities/entityLink/entityLink';
import { LinkedEntityConnection } from '../../entities/entityLink/linkedEntityConnection';
import { of } from 'rxjs';
import { IssueWidget } from '../../services/issue/issue-widget.interface';
import { WidgetCoordinatorService } from '../../services/issue/widget-coordinator.service';
import { RefetchCoordinator } from '../../services/refetch-coordinator/refetch-coordinator.service';
import { Reloadable } from '../../services/command/command.service';

@Component({
  selector: 'nx-single-entity-link-widget',
  templateUrl: './single-entity-link-widget.component.html',
  styleUrls: ['./single-entity-link-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleEntityLinkWidgetComponent implements OnDestroy, IssueWidget, Reloadable {

  entity = input.required<Linkable>();
  linkedEntityType = input.required<string>();
  linkType = input.required<string>();
  label = input.required<string>();
  placeholder = input.required<string>();
  @ViewChild('selectComponent') selectComponent: EditSelectInputComponent;

  editMode = signal(false);
  isLoading = signal(false);
  linkedEntityConnection = signal<LinkedEntityConnection | undefined>(undefined);
  options = signal<EditSelectable[]>([]);

  selectedControl = new FormControl<string | null>(null);
  form = new FormGroup({
    selected: this.selectedControl
  });

  private linkableService = inject(LinkableService);
  private apolloService = inject(ApolloService);
  private coordinator = inject(WidgetCoordinatorService);
  private destroyRef = inject(DestroyRef);
  private refetchCoordinator = inject(RefetchCoordinator);

  private widgetId = crypto.randomUUID();
  private unregisterRefetch?: () => void;

  constructor() {
    // Subscribe to cancel all requests
    this.coordinator.cancelAll$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(exceptId => {
        if (exceptId !== this.widgetId) {
          this.cancel();
        }
      });

    // Watch for entity changes and reload link
    effect(() => {
      const currentEntity = this.entity();
      if (currentEntity?.id && currentEntity?.entityType) {
        this.loadLink();
        // Register with refetch coordinator when entity is available
        this.unregisterRefetch?.();
        this.unregisterRefetch = this.refetchCoordinator.register(
          this,
          currentEntity.entityType,
          currentEntity.id
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.unregisterRefetch?.();
  }

  reload(): void {
    this.loadLink();
  }

  edit() {
    this.coordinator.requestCancelAll(this.widgetId);
    this.editMode.set(true);
    const connection = this.linkedEntityConnection();
    if (connection) {
      this.selectedControl.setValue(connection.linkedEntity.id);
    }
    this.loadLinkables("");
    setTimeout(() => {
      this.selectComponent?.openAndFocusFilter();
    }, 10);
  }

  save() {
    this.editMode.set(false);
    if (this.selectedControl.value) {
      this.linkEntities();
    } else {
      const connection = this.linkedEntityConnection();
      if (connection) {
        this.linkableService.unlinkEntities(connection.linkId)
          .pipe(
            take(1),
            tap(() => {
              this.linkedEntityConnection.set(undefined);
              this.loadLink();
              const entity = this.entity();
              this.refetchCoordinator.emit({ entityType: entity.entityType, entityId: entity.id });
            }),
            catchError(error => {
              return of(null);
            })
          )
          .subscribe();
      }
    }
  }

  onFilter = (filterValue: string) => {
    this.loadLinkables(filterValue);
  };

  private linkEntities(): void {
    this.isLoading.set(true);
    const entity = this.entity();
    let entityLinkInput: EntityLink = {
      outwardEntityId: entity.id,
      outwardEntityType: entity.entityType,
      inwardEntityId: this.selectedControl.value,
      inwardEntityType: this.linkedEntityType(),
      linkType: this.linkType()
    };

    const connection = this.linkedEntityConnection();
    if (connection?.linkId) {
      entityLinkInput.id = connection.linkId;
    }

    this.linkableService.linkEntities(entityLinkInput)
      .pipe(
        take(1),
        tap(result => {
          // After creating/updating, reload to get the LinkedEntityConnection format
          this.loadLink();
          this.refetchCoordinator.emit({ entityType: entity.entityType, entityId: entity.id });
          this.isLoading.set(false);
        }),
        catchError(error => {
          this.isLoading.set(false);
          return of(null);
        })
      )
      .subscribe();
  }

  private loadLinkables(filterText: string): void {
    // Create filter object for the service
    const linkableFilter: LinkableFilter = {
      entityTypes: [this.linkedEntityType()],
      text: filterText
      // Add other filter properties as needed based on your LinkableFilter interface
    };

    // Use the linkable service to get real data
    this.linkableService.getLinkables(linkableFilter)
      .pipe(
        map(linkables => linkables.map(linkable => ({
          id: linkable.id,
          name: linkable.text
        }))),
        catchError(error => {
          return of([]);
        })
      )
      .subscribe(options => {
        this.options.set(options);
      });
  }

  private loadLink(): void {
    const entity = this.entity();
    if (!entity?.id || !this.linkedEntityType()) {
      return;
    }
    this.apolloService.query<getLinkedEntitiesResponse>(GET_LINKED_ENTITIES_QUERY, {
      entityId: entity.id,
      linkedEntityType: this.linkedEntityType(),
      linkType: this.linkType(), // Add linkType filter if needed
      pagination: { number: 0, size: 10 }, // Add pagination
      sortRequest: { sortProperties: ["id"], sortDirection: "ASC" } // Add sorting
      })
      .pipe(
        take(1),
        map(result => {
          // Adapter: convert EntityLink to LinkedEntityConnection format
          const entityLinks = result.data.getLinkedEntities.content;
          if (entityLinks.length > 0) {
            const entityLink = new LinkedEntityConnection(entityLinks[0]);
            return entityLink;
          }
          return undefined;
        })
      )
      .subscribe({
        next: (connection) => {
          this.linkedEntityConnection.set(connection);
          this.isLoading.set(false);
        },
        error: (error) => {
          this.isLoading.set(false);
        }
      });
  }

  // this should later be refactored in a way that the link is not hardcoded,
  // but rather built dynamically based on the entity type via dependency injection
  buildEntityDetailsUrl(connection: LinkedEntityConnection) {
    if (connection?.linkedEntity?.entityType === 'Contact') {
      return `/customers/customer/${connection.linkedEntity.id}`;
    }
    if (connection?.linkedEntity?.entityType === 'Resource') {
      return `/resources/resource/${connection.linkedEntity.id}`;
    }
    if (connection?.linkedEntity?.entityType === 'Order') {
      return `/orders/order/${connection.linkedEntity.id}`;
    }
    return '';
  }

  cancel() {
    this.editMode.set(false);
  }

}
